import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Pencil, Mail, KeyRound, ExternalLink, CalendarDays, FileText, Landmark, Loader2, Coins, UserCog } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Footer } from "@/components/Footer";
import { MemberAuthPanel } from "@/components/member/MemberAuthPanel";
import { EditProfileDialog, ChangeEmailDialog, SetPasswordDialog } from "@/components/member/ProfileDialogs";
import { BillCard } from "@/components/bills/BillTracker";

const WOW_URL = "https://thewowfactor.earth/";
const AAF_URL = "https://averageaf.com/";
const HUB_URL = "https://account.averageaf.com/";
const DOC_LABEL = { latest_pdf: "Framework document", latest_pdf_print: "Framework (print)", flyer: "Flyer", flyer_print: "Flyer (print)", cards_digital: "Palm cards pack", cards_print: "Palm cards (print)", terms: "Terms and Conditions", privacy: "Privacy Policy", charter: "Our Charter" };

const RULE_LABEL = { "ROAN-JOIN": "Joined ROAN as a F.E.R.M", "ROAN-LETTER-FIRST": "First F.E.R.M letter", "WOW-EVT-ATTEND": "Checked in at an event", "WOW-EVT-REGISTER": "Booked an event", "WOW-REF-WELCOME": "Joined with a referral code", "WOW-REF-REFERRER": "Referred a new member", "AAF-PROFILE-COMPLETE": "Completed Average AF profile" };

const PointsHistory = ({ history }) => (
  <ul className="divide-y divide-[#1B2A41]/10" data-testid="wow-points-history">
    {history.map((h) => (
      <li key={h.id} className="flex items-center gap-3 py-2 text-xs" data-testid={`wow-points-entry-${h.id}`}>
        <span className="font-type text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1B2A41]/5 text-[#5A7355] w-14 text-center">{h.site_id || "—"}</span>
        <span className="flex-1 min-w-0 truncate text-[#1B2A41]">{RULE_LABEL[h.reason] || h.note || h.reason || h.type}</span>
        <span className="text-[#5C6A79] hidden sm:inline">{h.created_at ? new Date(h.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : ""}</span>
        <span className={`font-type font-bold ${h.amount < 0 ? "text-[#E2705A]" : "text-[#5A7355]"}`}>{h.amount > 0 ? "+" : ""}{h.amount.toLocaleString("en-AU")}</span>
      </li>
    ))}
  </ul>
);

// Reads straight from the shared Firestore in the signed-in person's browser (owner-only rules). No server-side admin access.
export const useAafAccount = (linked) => {
  const [state, setState] = useState({ user: null, points: null, history: [], error: false });
  useEffect(() => {
    let unsubAuth = () => {}, unsubPoints = () => {};
    import("@/lib/firebase").then((fb) => {
      if (!fb.firebaseEnabled) return;
      unsubAuth = fb.onFbUser(async (user) => {
        unsubPoints();
        if (!user) return setState({ user: null, points: null, history: [], error: false });
        setState((s) => ({ ...s, user }));
        unsubPoints = fb.watchPoints(user.uid, (points) => setState((s) => ({ ...s, points, error: !!points.error })));
        try { setState((s) => ({ ...s, history: [] })); const history = await fb.fetchLedger(user.uid); setState((s) => ({ ...s, history })); } catch (e) { setState((s) => ({ ...s, error: true })); }
      });
    }).catch(() => {});
    return () => { unsubAuth(); unsubPoints(); };
  }, [linked]);
  return state;
};

const WowPointsPanel = ({ linked }) => {
  const { user, points, history, error } = useAafAccount(linked);
  const live = !!user && !error;
  return (
    <section data-testid="wow-points-panel" className="rounded-2xl bg-white border border-[#1B2A41]/10 shadow-sm p-5 sm:p-6 space-y-4">
    <div className="grid sm:grid-cols-[auto_1fr_auto] gap-5 items-center">
      {user?.photoURL ? <img src={user.photoURL} alt="" referrerPolicy="no-referrer" data-testid="member-avatar" className="w-14 h-14 rounded-2xl object-cover border-2 border-[#E2705A]" /> : <div className="w-14 h-14 rounded-2xl bg-[#1B2A41] text-[#FDFBF7] flex items-center justify-center"><Coins className="w-6 h-6 text-[#E2705A]" /></div>}
      <div>
        <p className="font-type text-[11px] uppercase tracking-[0.3em] text-[#5A7355]">Average AF account</p>
        {user ? (
          <>
            <p className="font-type font-bold text-2xl sm:text-3xl text-[#1B2A41] mt-1" data-testid="wow-points-balance">WOW Points: {live && points ? points.balance.toLocaleString("en-AU") : live ? "…" : "—"}</p>
            <p className="text-xs text-[#5C6A79] mt-1">
              {error ? "Points are temporarily unavailable — try again shortly" : points?.exists ? `Lifetime earned ${points.lifetime.toLocaleString("en-AU")} · one balance across every Average AF site, updated live` : "One balance across every Average AF site — your first points land as soon as you earn them"}
            </p>
          </>
        ) : linked ? (
          <>
            <p className="font-semibold text-[#1B2A41] mt-1">Sign in with your Average AF account to see your WOW Points</p>
            <p className="text-xs text-[#5C6A79] mt-1">Your ROAN membership is linked — sign out, then sign back in with "Average AF" (Google or email + password) to load your balance and history here.</p>
          </>
        ) : (
          <>
            <p className="font-semibold text-[#1B2A41] mt-1">Link your Average AF account to see your WOW Points</p>
            <p className="text-xs text-[#5C6A79] mt-1">Sign out, then sign back in with "Average AF" (Google or email + password) using this same email. One login will soon open every Average AF site.</p>
          </>
        )}
      </div>
      <div className="flex flex-wrap sm:flex-col gap-2 sm:items-stretch">
        {(user || linked) && (
          <a href={HUB_URL} target="_blank" rel="noopener noreferrer" data-testid="manage-aaf-account-link" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-[#1B2A41] hover:bg-[#2C3E55] text-[#FDFBF7] text-xs font-type font-bold uppercase tracking-wider transition-colors">
            <UserCog className="w-3.5 h-3.5" /> Manage my Average AF account
          </a>
        )}
        <a href={user || linked ? WOW_URL : AAF_URL} target="_blank" rel="noopener noreferrer" data-testid="wow-points-link" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full border border-[#1B2A41]/20 hover:border-[#E2705A] hover:text-[#E2705A] text-xs font-type font-bold uppercase tracking-wider text-[#1B2A41] transition-colors">
          {user || linked ? "Earn on WOW" : "About Average AF"} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
    {live && (
      <div className="pt-3 border-t border-[#1B2A41]/10">
        <p className="font-type text-[10px] uppercase tracking-[0.25em] text-[#5A7355] mb-1">Recent activity</p>
        {history.length ? <PointsHistory history={history} /> : <p className="text-xs text-[#5C6A79]" data-testid="wow-points-history-empty">No points activity yet — joining ROAN and writing your first F.E.R.M letter both earn WOW Points.</p>}
      </div>
    )}
    </section>
  );
};

const Panel = ({ title, icon: Icon, children, testId, action }) => (
  <section data-testid={testId} className="rounded-2xl bg-white border border-[#1B2A41]/10 shadow-sm p-5 sm:p-6">
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="font-type font-bold text-sm uppercase tracking-wider text-[#1B2A41] inline-flex items-center gap-2"><Icon className="w-4 h-4 text-[#E2705A]" /> {title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Row = ({ k, v, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#D8D2C4]/60 last:border-0 text-sm">
    <span className="text-[#5C6A79]">{k}</span>
    <span className="font-semibold text-[#1B2A41] text-right" data-testid={testId}>{v}</span>
  </div>
);

const Action = ({ onClick, icon: Icon, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#D8D2C4] hover:border-[#E2705A] hover:text-[#E2705A] text-xs font-semibold transition-colors">
    <Icon className="w-3.5 h-3.5" /> {children}
  </button>
);

export default function MyRoan() {
  const { member, signOut, signIn } = useMember();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const { data, refetch, isLoading } = useQuery({ queryKey: ["member-profile", member?.id, member?.electorate], queryFn: () => api.get("/member/profile").then((r) => r.data), enabled: !!member });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("echange");
    if (!token) return;
    window.history.replaceState({}, "", "/me");
    api.post("/member/email-change/confirm", { token })
      .then(({ data: d }) => { signIn(d.token, d.member); toast.success(`Email updated to ${d.member.email}`); })
      .catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const m = data?.member;
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1E2830]">
      <header className="bg-[#121E2B] text-[#FDFBF7] grain">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between gap-4">
          <Link to="/" data-testid="myroan-back" className="inline-flex items-center gap-2 text-xs font-type uppercase tracking-widest text-[#8FA58A] hover:text-[#E2705A]"><ArrowLeft className="w-4 h-4" /> Back to site</Link>
          <h1 className="font-type font-bold text-xl sm:text-2xl uppercase tracking-wider">My ROAN</h1>
          {member ? <button onClick={() => { signOut(); navigate("/"); }} data-testid="myroan-sign-out" className="inline-flex items-center gap-1.5 text-xs font-type uppercase tracking-widest text-[#8FA58A] hover:text-[#E2705A]"><LogOut className="w-4 h-4" /> Sign out</button> : <span className="w-20" />}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 space-y-6">
        {member === null && <p className="text-sm text-[#5C6A79]"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Checking your sign-in…</p>}
        {member === false && <div className="max-w-md mx-auto"><MemberAuthPanel /></div>}
        {member && (isLoading || !m) && <p className="text-sm text-[#5C6A79]"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading your profile…</p>}
        {member && m && (
          <>
            <section data-testid="member-number-card" className="rounded-2xl bg-[#1B2A41] text-[#FDFBF7] p-6 sm:p-8 grain relative overflow-hidden">
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#E2705A]/15 blur-3xl" />
              <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <p className="font-type text-[11px] uppercase tracking-[0.3em] text-[#8FA58A]">Your F.E.R.M member number</p>
                  <p className="mt-2 font-type font-bold text-4xl sm:text-5xl lg:text-6xl text-[#E2705A] tracking-wide" data-testid="member-number">{m.member_number || "—"}</p>
                  <p className="mt-3 text-sm text-[#FDFBF7]/80 max-w-xl leading-relaxed">
                    This is your key. You need it to access the ROAN meetings hosted on the WOW site — the WOW event calendar holds the hosting rights and asks for this number as your login. Keep it private; it's never shown on the public pages.
                  </p>
                  {m.previous_numbers?.length > 0 && <p className="mt-2 text-xs text-[#FDFBF7]/50">Retired numbers: {m.previous_numbers.join(", ")}</p>}
                </div>
                <a href={WOW_URL} target="_blank" rel="noopener noreferrer" data-testid="wow-link" className="inline-flex flex-col items-start gap-1 rounded-2xl bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] px-5 py-4 transition-colors">
                  <span className="font-type font-bold text-sm uppercase tracking-wider inline-flex items-center gap-2">Go to the WOW site <ExternalLink className="w-4 h-4" /></span>
                  <span className="text-xs text-[#FDFBF7]/85 max-w-[220px]">Where we host the meetings — and the recordings if you miss one.</span>
                </a>
              </div>
            </section>

            <WowPointsPanel linked={data.aaf_linked} />

            <div className="grid md:grid-cols-2 gap-6">
              <Panel title="My details" icon={Pencil} testId="member-details" action={<Action onClick={() => setDialog("profile")} icon={Pencil} testId="edit-profile-button">Edit</Action>}>
                <Row k="Name" v={m.name} testId="member-name" />
                <Row k="Email" v={m.email} testId="member-email" />
                <Row k="Postcode" v={m.postcode} testId="member-postcode" />
                <Row k="Electorate" v={<Link to={`/electorate/${m.electorate}`} className="underline hover:text-[#E2705A]">{m.electorate} ({m.state})</Link>} testId="member-electorate" />
                <Row k="District no." v={m.district_number} />
                <Row k="Group" v={`Group ${m.group_number}`} />
                <Row k="Member since" v={new Date(m.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })} />
                <div className="flex flex-wrap gap-2 mt-4">
                  <Action onClick={() => setDialog("email")} icon={Mail} testId="change-email-button">Change email</Action>
                  <Action onClick={() => setDialog("password")} icon={KeyRound} testId="set-password-button">{m.has_password ? "Change password" : "Set a password"}</Action>
                </div>
              </Panel>

              <Panel title="Your sitting member" icon={Landmark} testId="member-mp">
                {data.mp ? (
                  <>
                    <p className="text-lg font-semibold text-[#1B2A41]">{data.mp.name}</p>
                    <p className="text-sm text-[#5C6A79]">{data.mp.party}</p>
                    {data.mp.email && <a href={`mailto:${data.mp.email}`} className="text-sm text-[#E2705A] underline mt-2 block">{data.mp.email}</a>}
                    {data.mp.phone && <p className="text-sm text-[#5C6A79] mt-1">{data.mp.phone}</p>}
                  </>
                ) : <p className="text-sm text-[#5C6A79]">Not recorded yet for {m.electorate}.</p>}
                <Link to={`/electorate/${m.electorate}`} className="mt-4 flex items-center gap-1 text-xs font-type uppercase tracking-wider text-[#E2705A]">Open the {m.electorate} page <ExternalLink className="w-3 h-3" /></Link>
              </Panel>
            </div>

            <Panel title="Upcoming meetings" icon={CalendarDays} testId="member-meetings">
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                {["national", "state", "division"].map((t) => (
                  <div key={t}>
                    <p className="font-type text-[11px] uppercase tracking-wider text-[#5A7355] mb-2">{t === "national" ? "National call" : t === "state" ? `${m.state} meeting` : `${m.electorate} check-in`}</p>
                    {(data.meetings?.[t] || []).slice(0, 3).map((o) => <p key={o.starts_at} className="py-1 border-b border-[#D8D2C4]/60 last:border-0">{o.display}</p>)}
                    {!(data.meetings?.[t] || []).length && <p className="text-[#5C6A79]">None scheduled</p>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#5C6A79] mt-4">Meetings are hosted on the <a href={WOW_URL} target="_blank" rel="noopener noreferrer" className="underline">WOW site</a> — have your member number ready.</p>
            </Panel>

            <Panel title={`Bills I've logged or am watching in ${m.electorate}`} icon={FileText} testId="member-bills" action={<Link to={`/electorate/${m.electorate}#bills`} className="text-xs font-type uppercase tracking-wider text-[#E2705A]">Log a bill</Link>}>
              {data.bills?.length ? <div className="grid md:grid-cols-2 gap-3">{data.bills.map((b) => <BillCard key={b.id} b={b} showElectorate />)}</div> : <p className="text-sm text-[#5C6A79]">Nothing yet. Open your electorate page to log a bill or vote worth the group's attention.</p>}
            </Panel>

            <Panel title="Key documents" icon={FileText} testId="member-documents">
              <div className="flex flex-wrap gap-2">
                {(data.documents || []).map((d) => (
                  <a key={d.kind} href={`${API}/documents/${d.kind}/download`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#FAF8F2] border border-[#D8D2C4] hover:border-[#E2705A] text-sm">
                    <FileText className="w-4 h-4 text-[#E2705A]" /> {DOC_LABEL[d.kind] || d.label || d.kind}
                  </a>
                ))}
              </div>
            </Panel>

            {dialog === "profile" && <EditProfileDialog open onOpenChange={(o) => !o && setDialog(null)} member={m} onSaved={refetch} />}
            {dialog === "email" && <ChangeEmailDialog open onOpenChange={(o) => !o && setDialog(null)} member={m} />}
            {dialog === "password" && <SetPasswordDialog open onOpenChange={(o) => !o && setDialog(null)} member={m} />}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
